import type { JevQuestion } from "./jev-question.types";

/**
 * The shapes the Jev schema translator produces, kept apart from the translator
 * so the decoder and every caller can name them without importing the logic.
 */

/**
 * How one leaf of the caller's JSON schema is asked, and how its answer is read
 * back. Data only, so a plan can be asserted on directly.
 */
export type JevFieldPlan =
  | { readonly kind: "boolean"; readonly path: readonly string[] }
  | {
      readonly kind: "choice";
      readonly path: readonly string[];
      readonly options: readonly string[];
    }
  | {
      readonly kind: "integer-scale";
      readonly path: readonly string[];
      readonly minimum: number;
      readonly levels: number;
    }
  | {
      readonly kind: "number-scale";
      readonly path: readonly string[];
      readonly minimum: number;
      readonly maximum: number;
      readonly levels: number;
    };

/** One field the schema asks for that Jev cannot produce, and why. */
export interface JevSchemaRefusal {
  /** Dotted path of the field, `(root)` for the schema itself. */
  readonly field: string;
  readonly reason: string;
}

export type JevTranslation =
  | {
      readonly ok: true;
      readonly questions: Readonly<Record<string, JevQuestion>>;
      readonly fields: Readonly<Record<string, JevFieldPlan>>;
    }
  | { readonly ok: false; readonly refusals: readonly JevSchemaRefusal[] };

export type SchemaNode = Readonly<Record<string, unknown>>;

export function isSchemaNode(value: unknown): value is SchemaNode {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Dotted path of a field, `(root)` for the schema itself. */
export function fieldName(path: readonly string[]): string {
  return path.length === 0 ? "(root)" : path.join(".");
}
